import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  RadioGroup,
  Radio,
  Box,
  Typography,
  Divider,
  Slider,
  IconButton,
  InputAdornment,
  Switch,
  Alert,
} from '@mui/material';
import { GLYPHS } from '@/components/ui';
import { Timestamp } from 'firebase/firestore';
import { Employee } from '../../types/employee';
import { useEmployeeActions } from '../../hooks/useEmployeeActions';
import { useAccountBookStore } from '../../store/accountBookStore';
import { formatNumber, formatNumberWithCommas } from '../../utils/formatters';
import { getMinimumWageWarning, MINIMUM_WAGE_2025 } from '../../utils/wageValidation';
import {
  useNpsWorkerShare,
  useNhiEmployerShare,
  useEiUnemploymentWorker,
  useWciAverageRate,
} from '../../hooks/useRates';
import {
  resolvePayType,
  type EmployeePayType,
  isDailyWorkerPay,
  isBusinessServicePay,
  isRegularEmployee,
} from '../../types/employee';
import EmployeeCategorySelector, { type EmployeeCategory } from './EmployeeCategorySelector';
import EmployeePayPreviewCard from './EmployeePayPreviewCard';
import PayTypeBadge from './PayTypeBadge';

interface EmployeeFormProps {
  open: boolean;
  onClose: () => void;
  employee?: Employee | null;
  // userId는 useAccountBookStore에서 가져옴
}

const EmployeeForm: React.FC<EmployeeFormProps> = ({
  open,
  onClose,
  employee
}) => {
  const { createEmployee, updateEmployee, loading } = useEmployeeActions();
  const { userId, currentAccountBook } = useAccountBookStore();

  // 4대보험 default 요율 (Firestore SSOT, 미동기화 시 fallback)
  const defaultPensionRate = useNpsWorkerShare();
  const defaultHealthRate = useNhiEmployerShare();
  const defaultEmploymentRate = useEiUnemploymentWorker();
  const defaultIndustrialRate = useWciAverageRate();

  const [formData, setFormData] = useState<Partial<Employee>>({
    name: '',
    mp: 3,
    hourlyRate: 9620,
    monthlySalary: 0,
    isHourly: true,
    payType: 'hourly' as EmployeePayType,
    dailyRate: 0,
    payday: 25,
    weeklyOffDays: 1,
    weeklyWorkHours: 40,
    taxRate: 0,
    dependents: 1,
    hasFourInsurance: true,
    age: 20,
    isweeklyWork: false,
    account_number: currentAccountBook?.accountNumber || 1,
    hire_date: null,
    severance_pay: null,
    pensionRate: defaultPensionRate,
    healthInsuranceRate: defaultHealthRate,
    employmentInsuranceRate: defaultEmploymentRate,
    industrialAccidentRate: defaultIndustrialRate,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [wizardStep, setWizardStep] = useState<'category' | 'form'>('category');
  const [category, setCategory] = useState<EmployeeCategory | null>(null);
  const [hourlyRateConfirmOpen, setHourlyRateConfirmOpen] = useState(false);
  const [hourlyRateReason, setHourlyRateReason] = useState('');
  const [pendingEmployeeData, setPendingEmployeeData] = useState<Omit<Employee, 'id'> | null>(null);

  useEffect(() => {
    if (employee) {
      const {
        id: _id,
        ownerId: _ownerId,
        createdAt: _createdAt,
        updatedAt: _updatedAt,
        hire_date,
        hireDate,
        ...rest
      } = employee;

      setFormData({
        ...rest,
        mp: employee.mp && employee.mp <= 5 ? employee.mp : 3,
        hire_date: hire_date ?? hireDate ?? null,
        payType: resolvePayType(employee.payType, employee.isHourly),
        dailyRate: employee.dailyRate ?? 0,
      });
      const pt = resolvePayType(employee.payType, employee.isHourly);
      setCategory(
        pt === 'dailyWorker' ? 'dailyWorker' : pt === 'businessService' ? 'businessService' : 'regular',
      );
      setWizardStep('form');
    } else {
      setWizardStep('category');
      setCategory(null);
      setFormData({
        name: '',
        mp: 3,
        hourlyRate: MINIMUM_WAGE_2025, // 2025년 최저임금
        monthlySalary: 0,
        isHourly: true,
        payType: 'hourly',
        dailyRate: 0,
        payday: 25,
        weeklyOffDays: 1,
        weeklyWorkHours: 40,
        taxRate: 0,
        dependents: 1,
        hasFourInsurance: true,
        age: 20,
        isweeklyWork: false,
        account_number: currentAccountBook?.accountNumber || 1,
        hire_date: null,
        severance_pay: null,
        pensionRate: defaultPensionRate,
        healthInsuranceRate: defaultHealthRate,
        employmentInsuranceRate: defaultEmploymentRate,
        industrialAccidentRate: defaultIndustrialRate,
      });
    }
    setErrors({});
  }, [employee, open, currentAccountBook, defaultPensionRate, defaultHealthRate, defaultEmploymentRate, defaultIndustrialRate]);

  const resolvedPayType = resolvePayType(formData.payType, formData.isHourly ?? true);
  const isNonRegular =
    isDailyWorkerPay({ payType: resolvedPayType, isHourly: formData.isHourly ?? false }) ||
    isBusinessServicePay({ payType: resolvedPayType, isHourly: formData.isHourly ?? false });

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name?.trim()) {
      newErrors.name = '이름을 입력해주세요.';
    }

    if (isNonRegular) {
      if (!formData.dailyRate || formData.dailyRate <= 0) {
        newErrors.dailyRate =
          resolvedPayType === 'dailyWorker' ? '일당을 입력해주세요.' : '건별 단가를 입력해주세요.';
      }
    } else if (formData.isHourly) {
      if (!formData.hourlyRate || formData.hourlyRate <= 0) {
        newErrors.hourlyRate = '시급을 입력해주세요.';
      } else if (formData.hourlyRate < MINIMUM_WAGE_2025) {
        newErrors.hourlyRate = getMinimumWageWarning(formData.hourlyRate) || '';
      }
    } else {
    if (!formData.monthlySalary || formData.monthlySalary <= 0) {
        newErrors.monthlySalary = '월급을 입력해주세요.';
      }
    }

    if (!formData.payday || formData.payday < 1 || formData.payday > 31) {
      newErrors.payday = '급여일은 1-31일 사이로 입력해주세요.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm() || !userId) return;

    try {
      const sanitizedFormData = { ...formData } as Partial<Employee>;
      (Object.keys(sanitizedFormData) as Array<keyof Employee>).forEach((key) => {
        if (sanitizedFormData[key] === undefined) {
          delete sanitizedFormData[key];
        }
      });

      const hireDateValue = sanitizedFormData.hire_date as unknown;
      if (hireDateValue) {
        if (hireDateValue instanceof Timestamp) {
          const dateString = hireDateValue.toDate().toISOString().split('T')[0];
          sanitizedFormData.hire_date = dateString as any;
          sanitizedFormData.hireDate = dateString as any;
        } else if (hireDateValue instanceof Date) {
          const dateString = hireDateValue.toISOString().split('T')[0];
          sanitizedFormData.hire_date = dateString as any;
          sanitizedFormData.hireDate = dateString as any;
        } else if (typeof hireDateValue === 'string') {
          const dateString = hireDateValue.split('T')[0];
          sanitizedFormData.hire_date = dateString as any;
          sanitizedFormData.hireDate = dateString as any;
        } else if (
          typeof hireDateValue === 'object' &&
          hireDateValue !== null &&
          'toDate' in hireDateValue &&
          typeof (hireDateValue as any).toDate === 'function'
        ) {
          const dateString = (hireDateValue as any).toDate().toISOString().split('T')[0];
          sanitizedFormData.hire_date = dateString as any;
          sanitizedFormData.hireDate = dateString as any;
        }
      }

      const employeeData = {
        ...sanitizedFormData,
        payType: resolvedPayType,
        dailyRate: isNonRegular ? sanitizedFormData.dailyRate ?? 0 : 0,
        isHourly: resolvedPayType === 'hourly',
        ownerId: userId,
        account_number: currentAccountBook?.accountNumber || 1,
        // useEmployeeActions에서 사용하는 필드명도 포함
        accountNumber: currentAccountBook?.accountNumber || 1,
      } as Omit<Employee, 'id'>;

      // 시급 변경 확인 (직원 수정 시, 시급제인 경우)
      if (employee && employee.isHourly && sanitizedFormData.hourlyRate !== undefined) {
        const isHourlyRateChanged = sanitizedFormData.hourlyRate !== employee.hourlyRate;
        
        if (isHourlyRateChanged) {
          // 시급 변경 확인 다이얼로그 표시
          setPendingEmployeeData(employeeData);
          setHourlyRateConfirmOpen(true);
          return;
        }
      }

      // 시급 변경이 없거나 신규 직원인 경우 바로 저장
      if (employee) {
        await updateEmployee(employee.id, employeeData);
      } else {
        await createEmployee(employeeData);
      }

      onClose();
    } catch (error) {
      console.error('직원 저장 실패:', error);
    }
  };

  const handleHourlyRateConfirm = async () => {
    if (!pendingEmployeeData || !employee) return;

    try {
      await updateEmployee(employee.id, pendingEmployeeData, {
        updateType: 'fullUpdate',
        reason: hourlyRateReason.trim() || undefined,
      });

      setHourlyRateConfirmOpen(false);
      setHourlyRateReason('');
      setPendingEmployeeData(null);
      onClose();
    } catch (error) {
      console.error('직원 저장 실패:', error);
    }
  };

  const handleHourlyRateCancel = () => {
    setHourlyRateConfirmOpen(false);
    setHourlyRateReason('');
    setPendingEmployeeData(null);
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // 에러 메시지 제거
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const ageGroups = [
    { label: '20대', value: 20 },
    { label: '30대', value: 30 },
    { label: '40대', value: 40 },
    { label: '50대', value: 50 }
  ];

  const getAgeGroupValue = (age: number | undefined) => {
    if (!age) return 20;
    return Math.floor(age / 10) * 10;
  };

  const handleAgeGroupChange = (value: number) => {
    handleInputChange('age', value);
  };

  const formatCurrencyInput = (value?: number | null) => {
    if (value === undefined || value === null) return '';
    return formatNumber(value, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  };

  const parseCurrencyInput = (value: string) => {
    const numeric = value.replace(/[^0-9]/g, '');
    if (numeric === '') return undefined;
    return parseInt(numeric, 10);
  };

  const getHireDateInputValue = () => {
    const hireDate = formData.hire_date as any;
    if (!hireDate) return '';

    if (typeof hireDate === 'string') {
      return hireDate;
    }

    if (hireDate instanceof Date) {
      return hireDate.toISOString().split('T')[0];
    }

    if (hireDate && typeof hireDate.toDate === 'function') {
      return hireDate.toDate().toISOString().split('T')[0];
    }

    return '';
  };

  // MP 레벨별 설명
  const getMPLevelDescription = (level: number) => {
    switch (level) {
      case 1:
        return {
          text: "1 - 신입직원: 매장에서 혼자 일을 할 수 없고, 교육이 필요한 상태",
          color: 'var(--c-cool-300)',
        };
      case 2:
        return {
          text: "2 - 초급 직원: 기본적인 업무를 수행할 수 있지만, 여전히 관리와 도움이 필요한 단계",
          color: 'var(--primary-soft)',
        };
      case 3:
        return {
          text: "3 - 중급 직원: 대부분의 업무를 독립적으로 처리할 수 있으며, 일상적인 문제를 해결할 수 있는 수준",
          color: '#fff9c4',
        };
      case 4:
        return {
          text: "4 - 고급 직원: 매장의 주요 업무를 주도적으로 수행할 수 있으며, 다른 직원들을 교육하거나 관리할 수 있는 능력을 보유한 상태",
          color: '#ffe0b2',
        };
      case 5:
        return {
          text: "5 - 전문가: 매장 운영 전반에 대한 이해도가 높으며, 효율적인 업무 수행과 함께 매장 성과를 최적화할 수 있는 핵심 인재",
          color: '#ffcdd2',
        };
      default:
        return {
          text: "",
          color: 'var(--bg-base)',
        };
    }
  };

  const handleCategorySelect = (cat: EmployeeCategory) => {
    setCategory(cat);
    if (cat === 'dailyWorker') {
      handleInputChange('payType', 'dailyWorker');
      handleInputChange('isHourly', false);
    } else if (cat === 'businessService') {
      handleInputChange('payType', 'businessService');
      handleInputChange('isHourly', false);
    } else {
      handleInputChange('payType', formData.isHourly ? 'hourly' : 'monthly');
    }
    setWizardStep('form');
  };

  const previewGross =
    isNonRegular && formData.dailyRate
      ? formData.dailyRate
      : formData.isHourly
        ? formData.hourlyRate ?? 0
        : formData.monthlySalary ?? 0;

  return (
    <>
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: 'var(--bg-base)',
          color: 'var(--fg-strong)',
          borderRadius: 2,
          border: '1px solid var(--border-subtle)',
          boxShadow: '0 24px 48px rgba(15, 23, 42, 0.12)'
        }
      }}
    >
      <DialogTitle sx={{ 
        fontWeight: 600, 
        color: 'var(--fg-strong)', 
        fontSize: '1.3rem',
        textAlign: 'center',
        pb: 1
      }}>
        {employee ? '직원 정보 수정' : '새 직원 추가'}
      </DialogTitle>
      
      <DialogContent sx={{ px: 3, py: 2, backgroundColor: 'var(--bg-elev-1)' }}>
        {!employee && wizardStep === 'category' ? (
          <EmployeeCategorySelector value={category} onChange={handleCategorySelect} />
        ) : (
          <>
        {employee && (
          <Box sx={{ mb: 2 }}>
            <PayTypeBadge payType={resolvedPayType} />
          </Box>
        )}

        {/* 이름 */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body1" sx={{ mb: 1, color: 'var(--fg-strong)', fontWeight: 600 }}>
            이름
          </Typography>
          <TextField
            fullWidth
            placeholder="직원 이름을 입력하세요"
            value={formData.name || ''}
            onChange={(e) => handleInputChange('name', e.target.value)}
            error={!!errors.name}
            helperText={errors.name}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'var(--bg-base)',
                '& fieldset': {
                  borderColor: 'var(--c-cool-300)',
                },
                '&:hover fieldset': {
                  borderColor: 'var(--fg-tertiary)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'var(--c-cool-900)',
                },
                '& input': {
                  color: 'var(--fg-strong)',
                  '&::placeholder': {
                    color: 'var(--fg-tertiary)',
                    opacity: 1,
                  },
                },
              },
              '& .MuiFormHelperText-root': {
                color: 'var(--c-red-600)',
              },
            }}
          />
        </Box>

        {/* 급여 형태 — 정규 직원만 */}
        {!isNonRegular && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="body1" sx={{ mb: 1, color: 'var(--fg-strong)', fontWeight: 600 }}>
            급여 형태
          </Typography>
          <Box sx={{ 
            backgroundColor: 'var(--bg-muted)', 
            borderRadius: 1, 
            p: 1,
            display: 'flex',
            gap: 1
          }}>
            <FormControlLabel
              control={
                <Radio
                  checked={formData.isHourly === true}
                  onChange={() => {
                    handleInputChange('isHourly', true);
                    handleInputChange('payType', 'hourly');
                  }}
                  sx={{
                    color: 'var(--fg-secondary)',
                    '&.Mui-checked': {
                      color: 'var(--c-cool-900)',
                    },
                  }}
                />
              }
              label="시급제"
              sx={{ color: 'var(--fg-strong)', flex: 1 }}
            />
            <FormControlLabel
              control={
                <Radio
                  checked={formData.isHourly === false}
                  onChange={() => {
                    handleInputChange('isHourly', false);
                    handleInputChange('payType', 'monthly');
                  }}
                  sx={{
                    color: 'var(--fg-secondary)',
                    '&.Mui-checked': {
                      color: 'var(--c-cool-900)',
                    },
                  }}
                />
              }
              label="월급제"
              sx={{ color: 'var(--fg-strong)', flex: 1 }}
            />
          </Box>
        </Box>
        )}

        {/* 시급/월급/일당 */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body1" sx={{ mb: 1, color: 'var(--fg-strong)', fontWeight: 600 }}>
            {isNonRegular
              ? resolvedPayType === 'dailyWorker'
                ? '일당'
                : '건별 단가'
              : formData.isHourly
                ? '시급'
                : '월급'}
          </Typography>
          <TextField
            fullWidth
            placeholder={
              isNonRegular
                ? resolvedPayType === 'dailyWorker'
                  ? '일당을 입력하세요'
                  : '건별 단가를 입력하세요'
                : formData.isHourly
                  ? '시급을 입력하세요'
                  : '월급을 입력하세요'
            }
            type="text"
            value={formatCurrencyInput(
              isNonRegular
                ? formData.dailyRate
                : formData.isHourly
                  ? formData.hourlyRate
                  : formData.monthlySalary,
            )}
            onChange={(e) => {
              const parsedValue = parseCurrencyInput(e.target.value);
              if (isNonRegular) {
                handleInputChange('dailyRate', parsedValue);
              } else {
                handleInputChange(formData.isHourly ? 'hourlyRate' : 'monthlySalary', parsedValue);
              }
            }}
            error={
              isNonRegular
                ? !!errors.dailyRate
                : formData.isHourly
                  ? !!errors.hourlyRate
                  : !!errors.monthlySalary
            }
            helperText={
              isNonRegular
                ? errors.dailyRate
                : formData.isHourly 
                ? (errors.hourlyRate || (formData.hourlyRate && formData.hourlyRate < MINIMUM_WAGE_2025 
                    ? getMinimumWageWarning(formData.hourlyRate) 
                    : `최저임금: ${MINIMUM_WAGE_2025.toLocaleString('ko-KR')}`))
                : errors.monthlySalary
            }
            inputProps={{
              inputMode: 'numeric',
            }}
            InputProps={{
              endAdornment: (isNonRegular
                ? formData.dailyRate
                : formData.isHourly
                  ? formData.hourlyRate
                  : formData.monthlySalary) ? (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="지우기"
                    onClick={() =>
                      handleInputChange(
                        isNonRegular
                          ? 'dailyRate'
                          : formData.isHourly
                            ? 'hourlyRate'
                            : 'monthlySalary',
                        undefined,
                      )
                    }
                    sx={{ fontSize: 22, lineHeight: 1, color: 'var(--fg-secondary)' }}
                  >
                    {GLYPHS.close}
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'var(--bg-base)',
                '& fieldset': {
                  borderColor: 'var(--c-cool-300)',
                },
                '&:hover fieldset': {
                  borderColor: 'var(--fg-tertiary)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'var(--c-cool-900)',
                },
                '& input': {
                  color: 'var(--fg-strong)',
                  '&::placeholder': {
                    color: 'var(--fg-tertiary)',
                    opacity: 1,
                  },
                },
              },
              '& .MuiFormHelperText-root': {
                color: 'var(--c-red-600)',
              },
            }}
          />
        </Box>

        {/* 급여일 */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body1" sx={{ mb: 1, color: 'var(--fg-strong)', fontWeight: 600 }}>
            급여일
          </Typography>
          <TextField
            fullWidth
            placeholder="매월 급여 지급일을 입력하세요 (1-31)"
            type="number"
            value={formData.payday || ''}
            onChange={(e) => handleInputChange('payday', parseInt(e.target.value) || 0)}
            error={!!errors.payday}
            helperText={errors.payday}
            inputProps={{ min: 1, max: 31 }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'var(--bg-base)',
                '& fieldset': {
                  borderColor: 'var(--c-cool-300)',
                },
                '&:hover fieldset': {
                  borderColor: 'var(--fg-tertiary)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'var(--c-cool-900)',
                },
                '& input': {
                  color: 'var(--fg-strong)',
                  '&::placeholder': {
                    color: 'var(--fg-tertiary)',
                    opacity: 1,
                  },
                },
              },
              '& .MuiFormHelperText-root': {
                color: 'var(--c-red-600)',
              },
            }}
          />
        </Box>

        {/* MP (ManPower) */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Typography variant="body1" sx={{ color: 'var(--fg-strong)', fontWeight: 600 }}>
              MP (ManPower)
            </Typography>
            <Typography sx={{ color: 'var(--fg-tertiary)', ml: 1 }}>?</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Slider
                value={formData.mp || 3}
                onChange={(_, value) => handleInputChange('mp', value)}
                min={1}
                max={5}
                step={1}
                marks={[
                  { value: 1, label: '1' },
                  { value: 2, label: '2' },
                  { value: 3, label: '3' },
                  { value: 4, label: '4' },
                  { value: 5, label: '5' },
                ]}
                sx={{
                  color: 'var(--c-cool-900)',
                  '& .MuiSlider-thumb': {
                    backgroundColor: 'var(--c-cool-900)',
                  },
                  '& .MuiSlider-track': {
                    backgroundColor: 'var(--c-cool-900)',
                  },
                  '& .MuiSlider-rail': {
                    backgroundColor: 'var(--c-cool-300)',
                  },
                  '& .MuiSlider-mark': {
                    backgroundColor: 'var(--fg-tertiary)',
                  },
                  '& .MuiSlider-markLabel': {
                    color: 'var(--fg-secondary)',
                  },
                }}
              />
            </Box>
            <Box sx={{ 
              backgroundColor: 'var(--bg-muted)', 
              px: 2, 
              py: 1, 
              borderRadius: 1,
              minWidth: 40,
              textAlign: 'center'
            }}>
              <Typography variant="body1" sx={{ color: 'var(--fg-strong)' }}>
                {formData.mp || 3}
              </Typography>
            </Box>
          </Box>
          {/* MP 레벨 설명 */}
          <Box sx={{ 
            mt: 2,
            p: 1.5,
            backgroundColor: getMPLevelDescription(formData.mp || 3).color,
            borderRadius: 1,
          }}>
            <Typography variant="body2" sx={{ color: 'var(--fg-strong)' }}>
              {getMPLevelDescription(formData.mp || 3).text}
            </Typography>
          </Box>
        </Box>

        {/* 4대보험 */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body1" sx={{ mb: 1, color: 'var(--fg-strong)', fontWeight: 600 }}>
            4대보험
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.hasFourInsurance || false}
                  onChange={(e) => handleInputChange('hasFourInsurance', e.target.checked)}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: 'var(--c-cool-900)',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: 'var(--c-cool-900)',
                    },
                  }}
                />
              }
              label=""
            />
          </Box>
        </Box>

        {/* 부양가족 수 */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body1" sx={{ mb: 1, color: 'var(--fg-strong)', fontWeight: 600 }}>
            부양가족 수
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Slider
                value={formData.dependents || 1}
                onChange={(_, value) => handleInputChange('dependents', value)}
                min={0}
                max={10}
                step={1}
                sx={{
                  color: 'var(--primary)',
                  '& .MuiSlider-thumb': {
                    backgroundColor: 'var(--primary)',
                  },
                  '& .MuiSlider-track': {
                    backgroundColor: 'var(--primary)',
                  },
                  '& .MuiSlider-rail': {
                    backgroundColor: 'var(--primary-soft)',
                  },
                }}
              />
            </Box>
            <Box sx={{ 
              backgroundColor: 'var(--bg-muted)', 
              px: 2, 
              py: 1, 
              borderRadius: 1,
              minWidth: 60,
              textAlign: 'center'
            }}>
              <Typography variant="body1" sx={{ color: 'var(--fg-strong)' }}>
                {formData.dependents || 1}명
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* 연령대 */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body1" sx={{ mb: 1, color: 'var(--fg-strong)', fontWeight: 600 }}>
            연령대
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {ageGroups.map((group) => (
              <Button
                key={group.value}
                variant={getAgeGroupValue(formData.age) === group.value ? "contained" : "outlined"}
                onClick={() => handleAgeGroupChange(group.value)}
                sx={{
                  flex: 1,
                  backgroundColor: getAgeGroupValue(formData.age) === group.value ? 'var(--c-cool-900)' : 'transparent',
                  borderColor: 'var(--c-cool-300)',
                  color: getAgeGroupValue(formData.age) === group.value ? 'var(--fg-on-primary)' : 'var(--fg-strong)',
                  '&:hover': {
                    backgroundColor: getAgeGroupValue(formData.age) === group.value ? 'var(--c-cool-950)' : 'var(--bg-elev-1)',
                    borderColor: 'var(--fg-tertiary)',
                  },
                }}
              >
                {group.label}
              </Button>
            ))}
          </Box>
        </Box>

        {/* 입사날짜 */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body1" sx={{ mb: 1, color: 'var(--fg-strong)', fontWeight: 600 }}>
            입사날짜
          </Typography>
          <TextField
            fullWidth
            placeholder="입사날짜를 선택하세요 (선택사항)"
            type="date"
            value={getHireDateInputValue()}
            onChange={(e) => handleInputChange('hire_date', e.target.value ? Timestamp.fromDate(new Date(e.target.value)) : null)}
            InputLabelProps={{ shrink: true }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'var(--bg-base)',
                '& fieldset': {
                  borderColor: 'var(--c-cool-300)',
                },
                '&:hover fieldset': {
                  borderColor: 'var(--fg-tertiary)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'var(--c-cool-900)',
                },
                '& input': {
                  color: 'var(--fg-strong)',
                  '&::placeholder': {
                    color: 'var(--fg-tertiary)',
                  },
                },
              },
            }}
          />
          <Typography variant="caption" sx={{ color: 'var(--fg-secondary)', mt: 0.5, display: 'block' }}>
            * 입사날짜를 입력하지 않으면 첫 근무일이 입사날짜로 설정됩니다.
          </Typography>
        </Box>

        {/* 주휴수당 포함 — 정규 직원만 */}
        {isRegularEmployee({ payType: resolvedPayType, isHourly: formData.isHourly ?? false }) && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body1" sx={{ mb: 1, color: 'var(--fg-strong)', fontWeight: 600 }}>
            주휴수당 포함
          </Typography>
          <RadioGroup
            row
            value={formData.isweeklyWork ? 'included' : 'separate'}
            onChange={(e) => handleInputChange('isweeklyWork', e.target.value === 'included')}
          >
            <FormControlLabel
              value="included"
              control={
                <Radio
                  sx={{
                    color: 'var(--fg-secondary)',
                    '&.Mui-checked': {
                      color: 'var(--c-cool-900)',
                    },
                  }}
                />
              }
              label="주휴포함"
              sx={{ color: 'var(--fg-strong)' }}
            />
            <FormControlLabel
              value="separate"
              control={
                <Radio
                  sx={{
                    color: 'var(--fg-secondary)',
                    '&.Mui-checked': {
                      color: 'var(--c-cool-900)',
                    },
                  }}
                />
              }
              label="주휴별도"
              sx={{ color: 'var(--fg-strong)' }}
            />
          </RadioGroup>
        </Box>
        )}

        {isNonRegular && previewGross > 0 && (
          <EmployeePayPreviewCard
            name={formData.name || undefined}
            payType={resolvedPayType}
            grossAmount={previewGross}
            employee={formData as Partial<Employee>}
            insuranceWorkDays={1}
          />
        )}
          </>
        )}
      </DialogContent>
      
      <DialogActions sx={{ p: 3, gap: 1 }}>
        {!employee && wizardStep === 'form' && (
          <Button
            onClick={() => setWizardStep('category')}
            variant="text"
            sx={{ color: 'var(--fg-secondary)', mr: 'auto' }}
          >
            이전
          </Button>
        )}
        <Button 
          onClick={onClose} 
          variant="outlined"
          sx={{
            borderColor: 'var(--c-cool-300)',
            color: 'var(--fg-strong)',
            '&:hover': {
              borderColor: 'var(--fg-tertiary)',
              backgroundColor: 'var(--bg-elev-1)',
            },
          }}
        >
          취소
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || (!employee && wizardStep === 'category')}
          sx={{
            backgroundColor: 'var(--c-cool-900)',
            '&:hover': {
              backgroundColor: 'var(--c-cool-950)',
            },
            '&:disabled': {
              backgroundColor: 'var(--bg-muted)',
              color: 'var(--fg-disabled)',
            },
          }}
        >
          {loading ? '저장 중...' : employee ? '수정' : '추가'}
        </Button>
      </DialogActions>
    </Dialog>

    {/* 시급 변경 확인 다이얼로그 */}
    <Dialog
      open={hourlyRateConfirmOpen}
      onClose={handleHourlyRateCancel}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          시급 변경 확인
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 3 }}>
          기존 스케줄의 시급도 모두 변경된 시급으로 업데이트됩니다.
        </Alert>

        {employee && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              시급 변경
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {formatNumberWithCommas(employee.hourlyRate)} → {formData.hourlyRate ? formatNumberWithCommas(formData.hourlyRate) : '0'}
            </Typography>
          </Box>
        )}

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            변경 사유 (선택사항)
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="변경 사유를 입력하세요"
            value={hourlyRateReason}
            onChange={(e) => setHourlyRateReason(e.target.value)}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3, gap: 1 }}>
        <Button
          onClick={handleHourlyRateCancel}
          variant="outlined"
          disabled={loading}
        >
          취소
        </Button>
        <Button
          onClick={handleHourlyRateConfirm}
          variant="contained"
          disabled={loading}
        >
          확인
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default EmployeeForm;
